/**
 * Picker integration-gating composable.
 *
 * Centralizes the three duplicated concerns of the predefined pickers
 * (CampaignAutomationSelectorV2, CampaignsBulkBaseSelectorV2,
 * TemplateSelectorV2):
 *
 *   1. Local modal state for `RequireIntegrationDialog` (visible, data,
 *      status).
 *   2. Pre-flight check before sending the import call — if the item
 *      belongs to an integration that isn't `active` on this shop, open
 *      the dialog and tell the caller to abort.
 *   3. Backend safety net — when the import returns
 *      `{ success: false, error: 'integration_required' }` (the shop
 *      deactivated the integration between page load and click), open
 *      the dialog with whatever metadata the backend echoes back.
 *
 * Plus a small `buildAuthorFilterOptions` factory that materializes the
 * "Auteur" multiselect (always includes ShopiMind + one entry per
 * distinct integration appearing in the loaded items).
 */

import { ref, computed } from 'vue';
import type { ComputedRef } from 'vue';
import type {
  IntegrationOwnedItem,
  RequireIntegrationInfo,
} from '@/types/integrations';
import type { RequireIntegrationStatus } from '@/components/integrations/RequireIntegrationDialog.vue';

/** Shape of the backend 409 returned by `/workflow/create-from-base` when
 *  the chosen base item belongs to an integration that isn't `active`. */
interface IntegrationRequiredResponse {
  success: false;
  error: 'integration_required';
  integration_slug?: string | null;
  integration_name?: string | null;
  integration_icon_url?: string | null;
  current_status?: RequireIntegrationStatus | null;
}

/** An author option in the picker's "Auteur" dropdown. Slug for
 *  integrations, the literal `'shopimind'` for native items, or an
 *  arbitrary extra option (e.g. `'me'` in the template selector). */
export interface AuthorFilterOption {
  value: string;
  label: string;
  /** Number of items for this author. Only set for integrations. */
  count?: number;
}

export function useIntegrationGating() {
  const requireIntegrationVisible = ref(false);
  const requireIntegrationData = ref<RequireIntegrationInfo | null>(null);
  const requireIntegrationStatus = ref<RequireIntegrationStatus>('not_installed');

  /**
   * Inspect the user-clicked item BEFORE making the backend call.
   *
   * Returns `true` when the gate fires (caller must abort the import).
   * Returns `false` for items that can proceed normally (ShopiMind items,
   * or integration items whose `shop_integration_status === 'active'`).
   */
  function gatePreflight(record: IntegrationOwnedItem): boolean {
    if (
      record.author === 'integration'
      && record.shop_integration_status !== 'active'
    ) {
      requireIntegrationData.value = {
        slug: record.integration_slug ?? '',
        name: record.integration_name ?? record.integration_slug ?? '',
        icon_url: record.integration_icon_url ?? null,
        short_description: record.integration_short_description ?? null,
      };
      requireIntegrationStatus.value = (record.shop_integration_status as RequireIntegrationStatus | null)
        ?? 'not_installed';
      requireIntegrationVisible.value = true;
      return true;
    }
    return false;
  }

  /**
   * Inspect the backend response AFTER the import call. Backend keeps a
   * safety net because the shop might have deactivated the integration
   * between page load and click — pre-flight wouldn't have caught it.
   *
   * Returns `true` when the gate fires (caller must abort post-success
   * processing). Returns `false` for any other shape (caller continues
   * with its usual success/error branching).
   *
   * The `record` is used as a fallback for fields the backend didn't echo
   * back — typically the icon_url, since it's only on the original item.
   */
  function gateBackendResponse(
    result: unknown,
    record: IntegrationOwnedItem,
  ): boolean {
    if (!result || typeof result !== 'object') return false;
    const r = result as IntegrationRequiredResponse;
    if (r.success === false && r.error === 'integration_required') {
      requireIntegrationData.value = {
        slug: r.integration_slug ?? record.integration_slug ?? '',
        name: r.integration_name ?? record.integration_name ?? '',
        icon_url: r.integration_icon_url ?? record.integration_icon_url ?? null,
        short_description: record.integration_short_description ?? null,
      };
      requireIntegrationStatus.value = (r.current_status as RequireIntegrationStatus | null) ?? 'not_installed';
      requireIntegrationVisible.value = true;
      return true;
    }
    return false;
  }

  /**
   * Build the "Auteur" multiselect options.
   *
   * `extraOptions` lets callers prepend custom rows (e.g. the
   * `{ value: 'me', label: t('templateSelector.filters.authorMe') }`
   * row in TemplateSelectorV2). They're inserted after ShopiMind and
   * before the dynamic integration rows.
   */
  function buildAuthorFilterOptions(
    items: IntegrationOwnedItem[],
    extraOptions: AuthorFilterOption[] = [],
  ): AuthorFilterOption[] {
    const options: AuthorFilterOption[] = [
      { value: 'shopimind', label: 'ShopiMind' },
      ...extraOptions,
    ];
    // Count items per integration so the filter can show "{name} · {n}".
    const counts = new Map<string, { label: string; count: number }>();
    for (const item of items) {
      const slug = item.integration_slug;
      if (item.author === 'integration' && slug) {
        const entry = counts.get(slug)
          ?? { label: item.integration_name || slug, count: 0 };
        entry.count += 1;
        counts.set(slug, entry);
      }
    }
    for (const [slug, { label, count }] of counts) {
      options.push({ value: slug, label, count });
    }
    return options;
  }

  /**
   * Convenience overload that accepts a `Ref`-style reactive source —
   * useful when the picker stores its loaded list in a `ref(…)`. Wraps
   * `buildAuthorFilterOptions` into a `computed` so the dropdown auto-
   * updates when items load asynchronously.
   */
  function authorFilterOptionsFor(
    itemsGetter: () => IntegrationOwnedItem[],
    extraOptionsGetter: () => AuthorFilterOption[] = () => [],
  ): ComputedRef<AuthorFilterOption[]> {
    return computed(() => buildAuthorFilterOptions(
      itemsGetter(),
      extraOptionsGetter(),
    ));
  }

  return {
    // State (bind directly to <RequireIntegrationDialog />)
    requireIntegrationVisible,
    requireIntegrationData,
    requireIntegrationStatus,
    // Gating logic
    gatePreflight,
    gateBackendResponse,
    // Author filter
    buildAuthorFilterOptions,
    authorFilterOptionsFor,
  };
}
